<template>
  <div class="admin-lead-form-show p-4 sm:p-6 lg:p-8">
    <!-- Header -->
    <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
      <div>
        <h1 class="text-3xl font-bold text-gray-900">{{ form.title }}</h1>
        <p class="text-gray-600 mt-1">Lead form details and analytics</p>
      </div>
      <div class="flex items-center gap-2">
        <Link
          :href="route('admin.lead-forms.index')"
          class="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
        >
          Back to List
        </Link>
        <Link
          :href="route('admin.lead-forms.submissions', form.id)"
          class="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
        >
          View Submissions
        </Link>
        <button
          @click="deleteForm"
          class="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          Delete
        </button>
      </div>
    </div>

    <!-- Form Information -->
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <!-- Main Details -->
      <div class="lg:col-span-2 space-y-6">
        <div class="bg-white rounded-lg shadow-md border border-gray-200 p-6">
          <h2 class="text-xl font-bold text-gray-900 mb-4">Form Information</h2>
          <div class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Title</label>
              <p class="text-gray-900">{{ form.title }}</p>
            </div>
            <div v-if="form.description">
              <label class="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <p class="text-gray-900">{{ form.description }}</p>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Slug</label>
              <code class="px-3 py-2 bg-gray-100 rounded text-sm">{{ form.slug }}</code>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <span
                :class="[
                  'inline-block px-3 py-1 text-sm font-medium rounded-full',
                  form.is_active
                    ? 'bg-green-100 text-green-800'
                    : 'bg-gray-100 text-gray-800'
                ]"
              >
                {{ form.is_active ? 'Active' : 'Inactive' }}
              </span>
            </div>
            <div v-if="form.redirect_url">
              <label class="block text-sm font-medium text-gray-700 mb-1">Redirect URL</label>
              <a :href="form.redirect_url" target="_blank" class="text-blue-600 hover:text-blue-800 hover:underline">
                {{ form.redirect_url }}
              </a>
            </div>
            <div v-if="form.success_message">
              <label class="block text-sm font-medium text-gray-700 mb-1">Success Message</label>
              <p class="text-gray-900">{{ form.success_message }}</p>
            </div>
          </div>
        </div>

        <div v-if="form.fields && form.fields.length > 0" class="bg-white rounded-lg shadow-md border border-gray-200 p-6">
          <h2 class="text-xl font-bold text-gray-900 mb-4">Form Fields</h2>
          <div class="space-y-3">
            <div
              v-for="(field, index) in form.fields"
              :key="index"
              class="p-4 bg-gray-50 rounded-lg border border-gray-200"
            >
              <div class="flex items-center justify-between mb-2">
                <span class="text-sm font-medium text-gray-700">{{ field.label || 'Unnamed Field' }}</span>
                <span class="text-xs text-gray-500">{{ field.type || 'text' }}</span>
              </div>
              <div v-if="field.required" class="text-xs text-red-600">Required</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Sidebar -->
      <div class="space-y-6">
        <!-- Analytics -->
        <div class="bg-white rounded-lg shadow-md border border-gray-200 p-6">
          <h2 class="text-xl font-bold text-gray-900 mb-4">Analytics</h2>
          <div class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Total Views</label>
              <p class="text-2xl font-bold text-blue-600">{{ form.views || 0 }}</p>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Submissions</label>
              <p class="text-2xl font-bold text-green-600">{{ form.submissions || 0 }}</p>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Conversion Rate</label>
              <p class="text-2xl font-bold text-purple-600">{{ form.conversion_rate || 0 }}%</p>
            </div>
          </div>
        </div>

        <!-- User Information -->
        <div class="bg-white rounded-lg shadow-md border border-gray-200 p-6">
          <h2 class="text-xl font-bold text-gray-900 mb-4">Form Owner</h2>
          <div class="space-y-2" v-if="form.user">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <p class="text-gray-900">{{ form.user.name }}</p>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <a :href="'mailto:' + form.user.email" class="text-green-600 hover:text-green-800 hover:underline">
                {{ form.user.email }}
              </a>
            </div>
          </div>
          <p v-else class="text-gray-500 text-sm">Guest user</p>
        </div>

        <!-- Dates -->
        <div class="bg-white rounded-lg shadow-md border border-gray-200 p-6">
          <h2 class="text-xl font-bold text-gray-900 mb-4">Dates</h2>
          <div class="space-y-2">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Created</label>
              <p class="text-gray-900 text-sm">{{ form.created_at }}</p>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Last Updated</label>
              <p class="text-gray-900 text-sm">{{ form.updated_at }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Link, router } from '@inertiajs/vue3'
import Icon from '@/Shared/Icon.vue'
import Layout from '@/Shared/Layout'

export default {
  layout: Layout,
  components: {
    Link,
    Icon,
  },
  props: {
    form: {
      type: Object,
      required: true,
    },
    title: {
      type: String,
      default: 'Lead Form Details',
    },
  },
  methods: {
    deleteForm() {
      if (confirm('Are you sure you want to delete this lead form? This action cannot be undone.')) {
        router.delete(route('admin.lead-forms.destroy', this.form.id), {
          onSuccess: () => {
            router.visit(route('admin.lead-forms.index'))
            if (window.$toast) {
              window.$toast.success('Lead form deleted successfully')
            }
          },
        })
      }
    },
  },
}
</script>

<style scoped>
.admin-lead-form-show {
  min-height: 100vh;
  background-color: #f9fafb;
}
</style>



